import axios, {type AxiosInstance, type InternalAxiosRequestConfig} from 'axios';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

/**
 * Shared Axios instance pre-configured for the Resource Planning backend.
 *
 * - Base URL points to the /api proxy (handled by Vite in dev, nginx in prod).
 * - An interceptor attaches the monday.com session token as a Bearer header
 *   so the backend can verify the caller.
 * - A response interceptor surfaces readable error messages.
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Request interceptor: attach session token ────────────────────────────────

apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const tokenResult = await monday.get('sessionToken');
      const token = tokenResult.data as string;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Send user's IANA timezone so the backend can validate dates correctly
      config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return config;
    },
);

// ─── Response interceptor: normalise errors ───────────────────────────────────

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      const message =
        (error.response?.data as { message?: string })?.message ??
        error.message;

      console.error('[api/client] Request failed:', {
        status: error.response?.status,
        url: error.config?.url,
        message,
      });

      // Re-throw with a cleaner message for UI consumption
      return Promise.reject(new Error(message));
    }
    return Promise.reject(error);
  },
);

export default apiClient;
